/**
 * Bulk import: template download, upload as a dry run, row-level review, duplicate resolution,
 * commit. Nothing is created until `commit` — upload only ever validates, which is why
 * `uploadImport` and `resolveImportRows` both return the same `ImportBatch` shape rather than
 * anything catalogue-shaped.
 */

import { api } from '../api.js';
import { importBatchTag } from '../tags.js';
import type {
  CategoryId,
  ImportBatch,
  ImportBatchId,
  ImportDuplicateResolution,
  ImportRow,
  ImportRowStatus,
  ImportType,
  Paged,
  ShopId,
} from '../../types/api.js';

export type ListImportRowsParams = {
  readonly batchId: ImportBatchId;
  readonly status?: ImportRowStatus | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
};

export const importsApi = api.injectEndpoints({
  endpoints: (build) => ({
    listImportTypes: build.query<ImportType[], void>({
      query: () => '/imports/types',
    }),

    /** Binary `.xlsx` — read as a blob, since it needs the auth header a plain link can't send. */
    downloadImportTemplate: build.query<
      Blob,
      { importType: string; categoryId?: CategoryId | undefined; shopId?: ShopId | undefined }
    >({
      query: (params) => ({
        url: '/imports/template',
        params,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    uploadImport: build.mutation<
      ImportBatch,
      { file: File; importType: string; shopId?: ShopId | undefined; categoryId?: CategoryId | undefined }
    >({
      query: ({ file, importType, shopId, categoryId }) => {
        const body = new FormData();
        body.append('file', file);
        body.append('importType', importType);
        if (shopId !== undefined) body.append('shopId', shopId);
        if (categoryId !== undefined) body.append('categoryId', categoryId);
        return { url: '/imports', method: 'POST', body };
      },
    }),

    getImportBatch: build.query<ImportBatch, ImportBatchId>({
      query: (id) => `/imports/${id}`,
      providesTags: (_result, _error, id) => [importBatchTag(id)],
    }),

    listImportRows: build.query<Paged<ImportRow>, ListImportRowsParams>({
      query: ({ batchId, ...params }) => ({
        url: `/imports/${batchId}/rows`,
        params: { page: 1, pageSize: 50, ...params },
      }),
    }),

    resolveImportRows: build.mutation<
      ImportBatch,
      { batchId: ImportBatchId; resolutions: Readonly<Record<number, ImportDuplicateResolution>> }
    >({
      query: ({ batchId, resolutions }) => ({
        url: `/imports/${batchId}/resolutions`,
        method: 'POST',
        body: { resolutions },
      }),
      invalidatesTags: (_result, _error, { batchId }) => [importBatchTag(batchId)],
    }),

    commitImport: build.mutation<ImportBatch, ImportBatchId>({
      query: (id) => ({ url: `/imports/${id}/commit`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [importBatchTag(id)],
    }),

    cancelImport: build.mutation<void, ImportBatchId>({
      query: (id) => ({ url: `/imports/${id}/cancellation`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [importBatchTag(id)],
    }),
  }),
});

export const {
  useListImportTypesQuery,
  useLazyDownloadImportTemplateQuery,
  useUploadImportMutation,
  useGetImportBatchQuery,
  useListImportRowsQuery,
  useResolveImportRowsMutation,
  useCommitImportMutation,
  useCancelImportMutation,
} = importsApi;
